import { FilterResults, MappedEntity, SnakeMeta } from '@api';
import { LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD } from '@terminal/views/screener/results/constants';
import {
  LIVE_PRICE_COLUMN_META_VALUE_FIELD,
  PRICE_COLUMN_META_VALUE_FIELD,
} from '@terminal/views/screener/results/screener-table-utils';

import { LivePriceInfo } from '../use-live-price';

export const mapToPriceEntities = ({
  asset,
  isLivePriceSupported,
  lastKnownPrice = 0,
}: {
  asset?: MappedEntity;
  isLivePriceSupported: boolean;
  lastKnownPrice?: number;
}) => {
  const priceEntity = asset && {
    entity: {
      ticker: asset.ticker,
      tag: asset.tag,
      sub_class: asset.sub_class,
      primary_method: asset.primary_method,
      subscribable_ticker: asset.subscribable_ticker,
      trading_country: asset.trading_country,
      exchange: asset.exchange?.code,
    },
    lastKnownPrice,
    priceBefore: 0,
    isLivePriceSupported,
  };

  return priceEntity ? [priceEntity] : [];
};

export const parseEntityScreenerData = (entityScreenerData?: FilterResults) => {
  const entityScreenerOverviewMeta = entityScreenerData?.data?.[0]?.data?.[0];
  const entityScreenerPerformanceMeta =
    entityScreenerData?.data?.[1]?.data?.[0];
  const entityScreenerPreviousClose =
    entityScreenerOverviewMeta?.[PRICE_COLUMN_META_VALUE_FIELD];
  const entityScreenerPrice =
    entityScreenerOverviewMeta?.[LIVE_PRICE_COLUMN_META_VALUE_FIELD];
  const entityScreenerReturn =
    entityScreenerPerformanceMeta?.[
      LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD
    ];

  return {
    previousClose: entityScreenerPreviousClose,
    price: entityScreenerPrice,
    liveReturn1D: entityScreenerReturn,
  };
};

export const parseData = ({
  livePriceData,
  snakePriceData,
  entityScreenerPriceData,
}: {
  livePriceData?: LivePriceInfo;
  snakePriceData?: SnakeMeta;
  entityScreenerPriceData?: FilterResults;
}) => {
  const snakeTimestamp = snakePriceData?.last_timestamp;
  const snakeDate = snakeTimestamp
    ? new Date(snakeTimestamp / 1_000_000).toISOString()
    : undefined;

  const {
    price: entityScreenerPrice,
    liveReturn1D: entityScreenerReturn,
    previousClose,
  } = parseEntityScreenerData(entityScreenerPriceData);

  return {
    livePriceInfo: {
      price: livePriceData?.price,
      time: livePriceData?.time,
    },
    snakePriceInfo: {
      price: snakePriceData?.last_value ?? 0,
      time: snakeDate,
    },
    entityScreenerPriceInfo: {
      price: entityScreenerPrice?.value,
      time: entityScreenerPrice?.tooltip
        ? new Date(entityScreenerPrice.tooltip).toISOString()
        : undefined,
      calculated1dReturn: entityScreenerReturn?.value,
      previousClose,
    },
  };
};

export const calculateReturn = (
  currentPrice?: number,
  previousPrice?: number
) => {
  if (!currentPrice || !previousPrice) {
    return undefined;
  }

  return ((currentPrice - previousPrice) / previousPrice) * 100;
};

export const getIsLivePriceSupported = (asset?: MappedEntity) =>
  !!asset?.subscribable_ticker;
